import { useState, useRef, useEffect } from 'react';
import { Button, Card, Form, InputGroup, Spinner } from 'react-bootstrap';
// import { toast } from 'react-toastify';

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

interface ChatBotProps {
  isOpen: boolean;
  onToggle: () => void;
}

export const ChatBot = ({ isOpen, onToggle }: ChatBotProps) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: 'Hello! I\'m your helpdesk assistant. How can I help you today?',
      isUser: false,
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const getBotResponse = (userMessage: string): string => {
    const message = userMessage.toLowerCase();
    
    // Greeting responses
    if (message.includes('hello') || message.includes('hi') || message.includes('hey')) {
      return 'Hello! Welcome to our helpdesk system. I\'m here to help you with any questions about our support services.';
    }
    
    // Helpdesk related responses
    if (message.includes('ticket') || message.includes('support')) {
      return 'Our helpdesk system allows you to create, track, and manage support tickets. You can access it by logging in to your account.';
    }
    
    if (message.includes('login') || message.includes('sign in')) {
      return 'You can access the helpdesk by clicking the "Access Helpdesk" button above. Use your organization credentials to sign in.';
    }
    
    if (message.includes('create') && message.includes('ticket')) {
      return 'To create a new ticket, log in to the system and click "New Ticket" in the dashboard. You can categorize your request and provide detailed information.';
    }
    
    if (message.includes('status') || message.includes('track')) {
      return 'You can track your ticket status by logging into the system and viewing your tickets in the dashboard. You\'ll receive notifications for updates.';
    }
    
    if (message.includes('organization') || message.includes('org')) {
      return 'Each organization has its own helpdesk instance. Make sure you\'re using the correct organization slug when logging in.';
    }
    
    if (message.includes('agent') || message.includes('admin')) {
      return 'Agents and admins have access to additional features like ticket assignment, priority management, and organization settings.';
    }
    
    if (message.includes('rating') || message.includes('feedback')) {
      return 'You can rate your support experience after a ticket is resolved. This helps us improve our service quality.';
    }
    
    if (message.includes('mobile') || message.includes('phone')) {
      return 'Our helpdesk system is fully responsive and works great on mobile devices, tablets, and desktop computers.';
    }
    
    if (message.includes('saas') || message.includes('software')) {
      return 'This helpdesk system is designed specifically for SaaS companies to manage customer support tickets and deliver exceptional user experiences.';
    }
    
    // Default responses
    if (message.includes('help') || message.includes('assistance')) {
      return 'I\'m here to help! You can ask me about creating tickets, tracking status, logging in, or any other helpdesk-related questions.';
    }
    
    if (message.includes('thank') || message.includes('thanks')) {
      return 'You\'re welcome! Is there anything else I can help you with?';
    }
    
    if (message.includes('bye') || message.includes('goodbye')) {
      return 'Goodbye! Feel free to come back anytime if you need assistance. Have a great day!';
    }
    
    // Fallback response
    return 'I understand you\'re looking for help. Could you be more specific about what you need assistance with? I can help with tickets, login, organization setup, or general questions about our helpdesk system.';
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputMessage,
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    // Simulate typing delay
    setTimeout(() => {
      const botResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: getBotResponse(inputMessage),
        isUser: false,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, botResponse]);
      setIsTyping(false);
    }, 1000 + Math.random() * 1000); // 1-2 second delay
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  return (
    <>
      {/* Chat Toggle Button */}
      <Button
        variant="primary"
        className="chatbot-toggle position-fixed"
        style={{
          bottom: '20px',
          right: '20px',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          zIndex: 1000,
          boxShadow: '0 4px 12px rgba(0, 123, 255, 0.3)',
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1.5rem'
        }}
        onClick={onToggle}
      >
        {isOpen ? <i className="fas fa-times"></i> : <i className="fas fa-comments"></i>}
      </Button>

      {/* Chat Window */}
      {isOpen && (
        <Card
          className="chatbot-window position-fixed"
          style={{
            bottom: '90px',
            right: '20px',
            width: '350px',
            height: '500px',
            zIndex: 1001,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            border: 'none',
            borderRadius: '16px',
            overflow: 'hidden'
          }}
        >
          {/* Chat Header */}
          <Card.Header
            className="d-flex align-items-center justify-content-between"
            style={{
              background: 'linear-gradient(135deg, #007bff, #0056b3)',
              color: 'white',
              border: 'none',
              padding: '1rem'
            }}
          >
            <div className="d-flex align-items-center">
              <div
                className="me-3"
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <i className="fas fa-robot"></i>
              </div>
              <div>
                <h6 className="mb-0">Helpdesk Assistant</h6>
                <small className="opacity-75">Online</small>
              </div>
            </div>
            <Button
              variant="link"
              className="text-white p-0"
              onClick={onToggle}
              style={{ fontSize: '1.2rem' }}
            >
              <i className="fas fa-times"></i>
            </Button>
          </Card.Header>

          {/* Messages Area */}
          <Card.Body
            className="p-0"
            style={{
              height: '350px',
              overflowY: 'auto',
              background: '#f8f9fa'
            }}
          >
            <div className="p-3">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`d-flex mb-3 ${message.isUser ? 'justify-content-end' : 'justify-content-start'}`}
                >
                  <div
                    className={`message-bubble ${
                      message.isUser ? 'user-message' : 'bot-message'
                    }`}
                    style={{
                      maxWidth: '80%',
                      padding: '0.75rem 1rem',
                      borderRadius: message.isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                      background: message.isUser ? '#007bff' : '#e9ecef',
                      color: message.isUser ? 'white' : '#333',
                      fontSize: '0.9rem',
                      lineHeight: '1.4',
                      wordWrap: 'break-word'
                    }}
                  >
                    {message.text}
                  </div>
                </div>
              ))}
              
              {isTyping && (
                <div className="d-flex justify-content-start mb-3">
                  <div
                    className="message-bubble bot-message"
                    style={{
                      padding: '0.75rem 1rem',
                      borderRadius: '18px 18px 18px 4px',
                      background: '#e9ecef',
                      color: '#333',
                      fontSize: '0.9rem'
                    }}
                  >
                    <Spinner size="sm" className="me-2" />
                    Assistant is typing...
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </Card.Body>

          {/* Input Area */}
          <Card.Footer className="p-0 border-0">
            <Form onSubmit={handleSendMessage} className="p-3">
              <InputGroup>
                <Form.Control
                  ref={inputRef}
                  type="text"
                  placeholder="Type your message..."
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  style={{
                    border: '1px solid #dee2e6',
                    borderRadius: '20px 0 0 20px',
                    padding: '0.5rem 1rem',
                    fontSize: '0.9rem'
                  }}
                />
                <Button
                  type="submit"
                  variant="primary"
                  disabled={!inputMessage.trim() || isTyping}
                  style={{
                    borderRadius: '0 20px 20px 0',
                    border: 'none',
                    padding: '0.5rem 1rem'
                  }}
                >
                  <i className="fas fa-paper-plane"></i>
                </Button>
              </InputGroup>
            </Form>
          </Card.Footer>
        </Card>
      )}
    </>
  );
};
